import React, { useContext, useEffect, useReducer, useState } from "react";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";

import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import Divider from "@mui/material/Divider";

import { Badge, Collapse, List } from "@mui/material";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import PhoneInTalkOutlinedIcon from "@mui/icons-material/PhoneInTalkOutlined";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";
import { makeStyles } from 'tss-react/mui';
import { keyframes } from 'tss-react';
import HowToRegIcon from '@mui/icons-material/HowToReg';

import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';

import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ContactPhoneOutlinedIcon from "@mui/icons-material/ContactPhoneOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import HelpOutlineIcon from "@mui/icons-material/HelpOutlined";
import CodeRoundedIcon from "@mui/icons-material/CodeRounded";
import EventIcon from "@mui/icons-material/Event";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PeopleIcon from "@mui/icons-material/People";
import ListIcon from "@mui/icons-material/ListAlt";
import AnnouncementIcon from "@mui/icons-material/Announcement";
import ForumIcon from "@mui/icons-material/Forum";
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import { i18n } from "../translate/i18n";

import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { Can } from "../components/Can";
import { socketConnection } from "../services/socket";
import { isArray } from "lodash";
import api from "../services/api";
import toastError from "../errors/toastError";
import LoyaltyRoundedIcon from '@mui/icons-material/LoyaltyRounded';
import ArtTrackIcon from '@mui/icons-material/ArtTrack';
import { AllInclusive, AttachFile, BlurCircular, DeviceHubOutlined, Schedule } from '@mui/icons-material';
import ListItemButton from "@mui/material/ListItemButton";
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import RecentActorsOutlinedIcon from '@mui/icons-material/RecentActorsOutlined';
import QuickreplyOutlinedIcon from '@mui/icons-material/QuickreplyOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import HistoryIcon from '@mui/icons-material/History';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import ContactsOutlinedIcon from '@mui/icons-material/ContactsOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';

const pingAnimation = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(229,72,77,.65); }
  70% { box-shadow: 0 0 0 7px rgba(229,72,77,0); }
  100% { box-shadow: 0 0 0 0 rgba(229,72,77,0); }
`;

const itemButtonSx = {
  margin: "1px 8px",
  width: "calc(100% - 16px)",
  paddingTop: 0.75,
  paddingBottom: 0.75,
  paddingLeft: "14px",
  borderRadius: "8px",
  color: "rgba(255,255,255,0.72)",
  transition: "background-color .12s ease, color .12s ease",
  "& .MuiListItemIcon-root": {
    minWidth: 36,
    color: "rgba(255,255,255,0.6)",
    transition: "color .12s ease",
    "& svg": { fontSize: 20 },
  },
  "& .MuiListItemText-primary": {
    fontSize: 14,
    fontWeight: 500,
  },
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.06)",
  },
  "&.Mui-selected": {
    backgroundColor: "rgba(192,95,95,0.18)",
    color: "#fff",
    "& .MuiListItemIcon-root": { color: "#e08a8a" },
    "& .MuiListItemText-primary": { fontWeight: 600 },
    "&:hover": {
      backgroundColor: "rgba(192,95,95,0.24)",
    },
  },
};

function ListItemLink(props) {
  const { icon, primary, to, className } = props;
  const location = useLocation();

  const isActive =
    location.pathname === to || location.pathname.startsWith(to + "/");

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  return (
    <li>
      <ListItemButton
        component={renderLink}
        className={className}
        selected={isActive}
        sx={itemButtonSx}
      >
        {icon ? <ListItemIcon>{icon}</ListItemIcon> : null}
        <ListItemText primary={primary} />
      </ListItemButton>
    </li>
  );
}

function ListItemGroup({ icon, primary, childPaths = [], children }) {
  const location = useLocation();
  const isChildActive = childPaths.some((p) =>
    location.pathname.startsWith(p)
  );
  const [open, setOpen] = useState(isChildActive);

  useEffect(() => {
    if (isChildActive) setOpen(true);
  }, [isChildActive]);

  return (
    <li>
      <ListItemButton
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        selected={isChildActive && !open}
        sx={itemButtonSx}
      >
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText primary={primary} />
        {open ? (
          <ExpandLessIcon className="groupChevron" sx={{ fontSize: 18, color: "rgba(255,255,255,0.45)" }} />
        ) : (
          <ExpandMoreIcon className="groupChevron" sx={{ fontSize: 18, color: "rgba(255,255,255,0.45)" }} />
        )}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List
          component="div"
          disablePadding
          sx={{
            "& > li > .MuiListItemButton-root": {
              paddingLeft: "28px",
            },
          }}
        >
          {children}
        </List>
      </Collapse>
    </li>
  );
}

const reducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    const chats = action.payload;
    const newChats = [];

    if (isArray(chats)) {
      chats.forEach((chat) => {
        const chatIndex = state.findIndex((u) => u.id === chat.id);
        if (chatIndex !== -1) {
          state[chatIndex] = chat;
        } else {
          newChats.push(chat);
        }
      });
    }

    return [...state, ...newChats];
  }

  if (action.type === "UPDATE_CHATS") {
    const chat = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chat.id);

    if (chatIndex !== -1) {
      state[chatIndex] = chat;
      return [...state];
    } else {
      return [chat, ...state];
    }
  }

  if (action.type === "DELETE_CHAT") {
    const chatId = action.payload;

    const chatIndex = state.findIndex((u) => u.id === chatId);
    if (chatIndex !== -1) {
      state.splice(chatIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "CHANGE_CHAT") {
    const changedChats = state.map((chat) => {
      if (chat.id === action.payload.chat.id) {
        return action.payload.chat;
      }
      return chat;
    });
    return changedChats;
  }
};

const MainListItems = (props) => {
  const { drawerClose } = props;
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  const [connectionWarning, setConnectionWarning] = useState(false);
  const [showCampaigns, setShowCampaigns] = useState(false);
  const navigate = useNavigate();

  const [invisible, setInvisible] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam] = useState("");
  const [chats, dispatch] = useReducer(reducer, []);

  const [showKanban, setShowKanban] = useState(true);
  const [showOpenAi, setShowOpenAi] = useState(true);
  const [showIntegrations, setShowIntegrations] = useState(true);
  const [showSchedules, setShowSchedules] = useState(true);
  const [showInternalChat, setShowInternalChat] = useState(false);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchChats();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });

    socket.on(`company-${companyId}-chat`, (data) => {
      if (data.action === "new-message") {
        dispatch({ type: "CHANGE_CHAT", payload: data });
      }
      if (data.action === "update") {
        dispatch({ type: "CHANGE_CHAT", payload: data });
      }
    });
    return () => {
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    let unreadsCount = 0;
    if (chats.length > 0) {
      for (let chat of chats) {
        for (let chatUser of chat.users) {
          if (chatUser.userId === user.id) {
            unreadsCount += chatUser.unreads;
          }
        }
      }
    }
    if (unreadsCount > 0) {
      setInvisible(false);
    } else {
      setInvisible(true);
    }
  }, [chats, user.id]);

  useEffect(() => {
    if (localStorage.getItem("cshow")) {
      setShowCampaigns(true);
    }
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  const fetchChats = async () => {
    try {
      const { data } = await api.get("/chats/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CHATS", payload: data.records });
    } catch (err) {
      toastError(err);
    }
  };

  const useStyles = makeStyles()((theme) => ({
    connectionIconWrap: {
      position: "relative",
      display: "inline-flex",
    },
    warningDot: {
      position: "absolute",
      top: -2,
      right: -2,
      width: 8,
      height: 8,
      borderRadius: "50%",
      background: "#e5484d",
      border: "2px solid #b73a3a",
      animation: `${pingAnimation} 1.6s ease-out infinite`,
    },
  }));

  function ConnectionIcon({ warning }) {
    const { classes } = useStyles();
    return (
      <span className={classes.connectionIconWrap}>
        <SyncAltIcon />
        {warning && <span className={classes.warningDot} />}
      </span>
    );
  }

  return (
    <div onClick={drawerClose}>
      {}
      <ListItemLink
        to="/"
        primary={i18n.t("mainDrawer.listItems.dashboard")}
        icon={<DashboardOutlinedIcon />}
      />
      <ListItemLink
        to="/tickets"
        primary={i18n.t("mainDrawer.listItems.tickets")}
        icon={<WhatsAppIcon />}
      />
      <ListItemLink
        to="/contacts"
        primary={i18n.t("mainDrawer.listItems.contacts")}
        icon={<ContactsOutlinedIcon />}
      />
      {showSchedules && (
        <ListItemLink
          to="/schedules"
          primary={i18n.t("mainDrawer.listItems.schedules")}
          icon={<EventOutlinedIcon />}
        />
      )}
      <ListItemLink
        to="/chats"
        primary={i18n.t("mainDrawer.listItems.chats")}
        icon={
          <Badge color="secondary" variant="dot" invisible={invisible}>
            <ForumIcon />
          </Badge>
        }
      />
      <ListItemLink
        to="/helps"
        primary={i18n.t("mainDrawer.listItems.helps")}
        icon={<HelpOutlineIcon />}
      />

      {}
      {(user.profile === "supervisor" ||
        user.profile === "admin" ||
        showKanban) && (
        <ListItemGroup
          primary={i18n.t("mainDrawer.listItems.management")}
          icon={<CategoryOutlinedIcon />}
          childPaths={[
            "/kanban",
            "/kanbanContact",
            "/quick-messages",
            "/template-message",
            "/tags",
          ]}
        >
          {showKanban && (
            <>
              <ListItemLink
                to="/kanban"
                primary={i18n.t("mainDrawer.listItems.kanbanTags")}
                icon={<ViewKanbanOutlinedIcon />}
              />
              <ListItemLink
                to="/kanbanContact"
                primary={i18n.t("mainDrawer.listItems.kanbanContacts")}
                icon={<RecentActorsOutlinedIcon />}
              />
            </>
          )}
          {(user.profile === "supervisor" || user.profile === "admin") && (
            <>
              <ListItemLink
                to="/quick-messages"
                primary={i18n.t("mainDrawer.listItems.quickMessages")}
                icon={<QuickreplyOutlinedIcon />}
              />
              <ListItemLink
                to="/template-message"
                primary={i18n.t("mainDrawer.listItems.templateMessage")}
                icon={<ArticleOutlinedIcon />}
              />
              <ListItemLink
                to="/tags"
                primary={i18n.t("mainDrawer.listItems.tags")}
                icon={<LocalOfferIcon />}
              />
            </>
          )}
        </ListItemGroup>
      )}

      {}
      {(user.profile === "supervisor" || user.profile === "admin") && (
        <>
          <Divider />
          <ListSubheader
            disableSticky
            sx={{
              backgroundColor: "transparent",
              textTransform: "uppercase",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.08em",
              color: "rgba(255,255,255,0.4)",
              lineHeight: "32px",
              paddingLeft: "24px",
              marginTop: 1,
            }}
          >
            {i18n.t("mainDrawer.listItems.administration")}
          </ListSubheader>
        </>
      )}
      {(user.profile === "supervisor" || user.profile === "admin") && (
        <ListItemLink
          to="/connections"
          primary={i18n.t("mainDrawer.listItems.connections")}
          icon={<ConnectionIcon warning={connectionWarning} />}
        />
      )}
      {(user.profile === "supervisor" || user.profile === "admin") && (
        <ListItemLink
          to="/ad-campaigns"
          primary={i18n.t("mainDrawer.listItems.adCampaigns")}
          icon={<CampaignOutlinedIcon />}
        />
      )}
      {(user.profile === "supervisor" || user.profile === "admin") && (
        <ListItemGroup
          primary={i18n.t("mainDrawer.listItems.calls")}
          icon={<PhoneInTalkOutlinedIcon />}
          childPaths={["/calls/history", "/calls/metrics"]}
        >
          <ListItemLink
            to="/calls/history"
            primary={i18n.t("mainDrawer.listItems.callHistory")}
            icon={<HistoryIcon />}
          />
          <ListItemLink
            to="/calls/metrics"
            primary={i18n.t("mainDrawer.listItems.callMetrics")}
            icon={<QueryStatsOutlinedIcon />}
          />
        </ListItemGroup>
      )}
      {}
      {(user.profile === "supervisor" || user.profile === "admin") && (
        <ListItemGroup
          primary={i18n.t("mainDrawer.listItems.reports")}
          icon={<SupervisorAccountOutlinedIcon />}
          childPaths={["/auditor", "/ai-replies", "/activity", "/analytics"]}
        >
          <ListItemLink
            to="/auditor"
            primary={i18n.t("mainDrawer.listItems.auditor")}
            icon={<FactCheckOutlinedIcon />}
          />
          <ListItemLink
            to="/ai-replies"
            primary={i18n.t("mainDrawer.listItems.aiReplies")}
            icon={<FactCheckOutlinedIcon />}
          />
          <ListItemLink
            to="/activity"
            primary={i18n.t("mainDrawer.listItems.activity")}
            icon={<HistoryIcon />}
          />
          <ListItemLink
            to="/analytics"
            primary={i18n.t("mainDrawer.listItems.analytics")}
            icon={<BarChartOutlinedIcon />}
          />
        </ListItemGroup>
      )}

      <Can
        role={user.profile}
        perform="drawer-admin-items:view"
        yes={() => (
          <>
            {showCampaigns && (
              <ListItemGroup
                primary={i18n.t("mainDrawer.listItems.campaigns")}
                icon={<EventAvailableIcon />}
                childPaths={["/campaigns", "/contact-lists", "/campaigns-config"]}
              >
                <ListItemLink
                  to="/campaigns"
                  primary={i18n.t("mainDrawer.listItems.campaignsList")}
                  icon={<ListIcon />}
                />
                <ListItemLink
                  to="/contact-lists"
                  primary={i18n.t("contactLists.title")}
                  icon={<PeopleIcon />}
                />
                <ListItemLink
                  to="/campaigns-config"
                  primary={i18n.t("mainDrawer.listItems.campaignsSettings")}
                  icon={<SettingsOutlinedIcon />}
                />
              </ListItemGroup>
            )}
            <ListItemGroup
              primary={i18n.t("mainDrawer.listItems.automation")}
              icon={<SmartToyOutlinedIcon />}
              childPaths={[
                "/prompts",
                "/queue-integration",
                "/testing",
                "/data-sources",
                "/knowledge",
                "/ai-costs",
              ]}
            >
              {}
              {showOpenAi && (
                <>
                  <ListItemLink
                    to="/prompts"
                    primary={i18n.t("mainDrawer.listItems.prompts")}
                    icon={<PsychologyOutlinedIcon />}
                  />
                  <ListItemLink
                    to="/data-sources"
                    primary={i18n.t("mainDrawer.listItems.dataSources")}
                    icon={<StorageOutlinedIcon />}
                  />
                  <ListItemLink
                    to="/knowledge"
                    primary={i18n.t("mainDrawer.listItems.knowledge")}
                    icon={<MenuBookOutlinedIcon />}
                  />
                  <ListItemLink
                    to="/testing"
                    primary={i18n.t("mainDrawer.listItems.testing")}
                    icon={<ScienceOutlinedIcon />}
                  />
                  <ListItemLink
                    to="/ai-costs"
                    primary={i18n.t("mainDrawer.listItems.aiCosts")}
                    icon={<PaymentsOutlinedIcon />}
                  />
                </>
              )}
              {showIntegrations && (
                <ListItemLink
                  to="/queue-integration"
                  primary={i18n.t("mainDrawer.listItems.queueIntegration")}
                  icon={<DeviceHubOutlined />}
                />
              )}
            </ListItemGroup>
            {}
            <ListItemGroup
              primary={i18n.t("mainDrawer.listItems.company")}
              icon={<BusinessOutlinedIcon />}
              childPaths={[
                "/queues",
                "/users",
                "/financeiro",
                "/announcements",
                "/settings",
                "/messages-api",
              ]}
            >
              <ListItemLink
                to="/queues"
                primary={i18n.t("mainDrawer.listItems.queues")}
                icon={<AccountTreeOutlinedIcon />}
              />
              <ListItemLink
                to="/messages-api"
                primary={i18n.t("mainDrawer.listItems.api")}
                icon={<CodeRoundedIcon />}
              />
              <ListItemLink
                to="/users"
                primary={i18n.t("mainDrawer.listItems.users")}
                icon={<PeopleAltOutlinedIcon />}
              />
              <ListItemLink
                to="/financeiro"
                primary={i18n.t("mainDrawer.listItems.financeiro")}
                icon={<PaymentsOutlinedIcon />}
              />
              {user.super && (
                <ListItemLink
                  to="/announcements"
                  primary={i18n.t("mainDrawer.listItems.annoucements")}
                  icon={<CampaignOutlinedIcon />}
                />
              )}
              <ListItemLink
                to="/settings"
                primary={i18n.t("mainDrawer.listItems.settings")}
                icon={<SettingsOutlinedIcon />}
              />
            </ListItemGroup>
            {}
          </>
        )}
      />
    </div>
  );
};

export default MainListItems;
